import DashboardLayout from "@/layouts/DashboardLayout";
import { useApi } from "@/hooks/useApi";
import { useNotifications } from "@/context/NotificationContext";
import TicketModal from "./components/TicketModal";
import { useState } from "react";
import api from "@/services/api";

interface Ticket {
  id: string;
  subject: string;
  message: string;
  status: string;
  priority: string;
  created_at: string;
}

export default function DashboardTickets() {
  const { showToast } = useNotifications();
  const [filter, setFilter] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const query = filter ? `/tickets?status=${filter}` : "/tickets";
  const { data: tickets, loading, refetch } = useApi<Ticket[]>(query);

  const handleCreateTicket = async (data: any) => {
    await api.post("/tickets", data);
    showToast("success", "Ticket created successfully! We'll get back to you soon.");
    refetch();
  };

  const handleReply = async (ticketId: string, message: string) => {
    await api.post(`/tickets/${ticketId}/replies`, { message });
    showToast("success", "Reply sent successfully!");
  };

  const handleStatusUpdate = async (ticketId: string, status: string) => {
    await api.patch(`/tickets/${ticketId}`, { status });
    showToast("success", "Status updated successfully!");
    refetch();
  };

  const statusColors: Record<string, string> = {
    open: "text-cyber-orange border-cyber-orange/30 bg-cyber-orange/10",
    in_progress: "text-electric-blue border-electric-blue/30 bg-electric-blue/10",
    resolved: "text-cyber-green border-cyber-green/30 bg-cyber-green/10",
    closed: "text-silver-dim border-dark-border bg-dark-border/30",
  };

  const priorityColors: Record<string, string> = {
    low: "text-silver-dim",
    medium: "text-cyber-orange",
    high: "text-cyber-red",
    urgent: "text-cyber-red",
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Support Tickets</h1>
            <p className="text-silver text-sm mt-1">View and manage your support requests.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowModal(true)}
              className="flex items-center gap-2 bg-electric-blue text-deep-black font-semibold px-4 py-2.5 rounded-xl text-sm hover:shadow-glow-blue transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-add-line" /> Create Ticket
            </button>
            {["", "open", "in_progress", "resolved"].map((s) => (
              <button
                key={s || "all"}
                onClick={() => setFilter(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  filter === s ? "bg-electric-blue/20 text-electric-blue" : "text-silver-dim hover:text-white"
                }`}
              >
                {s ? s.replace("_", " ") : "All"}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-2 border-electric-blue border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="bg-dark-card border border-dark-border rounded-2xl overflow-hidden">
            <div className="divide-y divide-dark-border">
              {tickets?.map((ticket) => (
                <div key={ticket.id} className="p-5 hover:bg-dark-panel/50 transition-colors">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="text-white font-semibold text-sm">{ticket.subject}</h3>
                    <div className="flex gap-2 flex-shrink-0">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${statusColors[ticket.status]}`}>
                        {ticket.status.replace("_", " ")}
                      </span>
                    </div>
                  </div>
                  <p className="text-silver text-xs leading-relaxed mb-3">{ticket.message}</p>
                  <div className="flex items-center justify-between gap-3 text-xs">
                    <div className="flex gap-3">
                      <span className={priorityColors[ticket.priority]}>{ticket.priority} priority</span>
                      <span className="text-silver-dim">{new Date(ticket.created_at).toLocaleDateString()}</span>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedTicketId(ticket.id);
                        setViewModal(true);
                      }}
                      className="text-electric-blue hover:text-white text-xs font-medium cursor-pointer"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))}
              {(!tickets || tickets.length === 0) && (
                <div className="p-10 text-center">
                  <i className="ri-customer-service-line text-4xl text-silver-dim mb-3 block" />
                  <p className="text-silver text-sm">No tickets yet.</p>
                </div>
              )}
            </div>
          </div>
        )}

        <TicketModal
          open={showModal}
          mode="create"
          onClose={() => setShowModal(false)}
          onSubmit={handleCreateTicket}
        />

        <TicketModal
          open={viewModal}
          mode="view"
          ticketId={selectedTicketId || undefined}
          onClose={() => setViewModal(false)}
          onReply={handleReply}
          onUpdateStatus={handleStatusUpdate}
        />
      </div>
    </DashboardLayout>
  );
}

